import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatChartDetailValue,
  pegRatioColorClass,
} from '../pages/chartDetailFormatting';
import { buildChartImageUrl } from './chartImageUrl';

const ChartCard = ({ stock, currentFilters, currentPage, stockIndex, screenerNav }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageCacheKey] = useState(
    () => `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  // Get trend category
  const getTrendCategory = (stock) => {
    const trendState = parseInt(stock.trend_state);
    const signalScore = parseInt(stock.signal_score);
    const predictedScore = parseInt(stock.predicted_signal_score);

    if (trendState === 1) {
      // UP Trend
      if (signalScore >= 3 && predictedScore >= 3) {
        return { label: 'UpTrend-Strong', color: 'signal-bullish' };
      } else if (signalScore < 3 && predictedScore < 3) {
        return { label: 'UpTrend-Weakening', color: 'signal-neutral' };
      } else if (signalScore >= 3 && predictedScore === 0) {
        return { label: 'UpTrend-NearReversal', color: 'signal-bearish' };
      } else {
        return { label: 'UpTrend', color: 'signal-bullish' };
      }
    } else {
      // DOWN Trend
      if (signalScore < 3 && predictedScore < 3) {
        return { label: 'DownTrend-Declining', color: 'signal-bearish' };
      } else if (signalScore > 3 && predictedScore > 3) {
        return { label: 'DownTrend-Improving', color: 'signal-neutral' };
      } else if (signalScore > 3 && predictedScore === 5) {
        return { label: 'DownTrend-NearReversal', color: 'signal-bullish' };
      } else {
        return { label: 'DownTrend', color: 'signal-bearish' };
      }
    }
  };

  const trendCategory = getTrendCategory(stock);

  // Handle image loading error
  const handleImageError = () => {
    setImageError(true);
  };

  // Get image URL
  const getImageUrl = () => {
    const BASE_IMG_URL = process.env.REACT_APP_CHART_IMAGE_BASE_URL || 'https://www.swingtradertrends.com/rj/img/';
    return buildChartImageUrl(stock, BASE_IMG_URL, imageCacheKey);
  };

  const imageUrl = getImageUrl();

  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  // Handle chart image click
  const handleChartClick = () => {
    navigate(`/chart/${stock.id}`, {
      state: {
        filters: currentFilters || {},
        page: currentPage || 1,
        stockIndex: stockIndex || 0,
        // Screener entry point: its client-side filter/sort state so the
        // detail view's Next/Previous walk the filtered set (see chartNav).
        screener: screenerNav || null,
      }
    });
  };

  // Compact metric row — mirrors the detail view's MetricRow but in xs text
  const CardMetricRow = ({ label, dataKey, colorFn }) => {
    const val = stock[dataKey];
    if (val === undefined || val === null || val === '') return null;
    const formatted = formatChartDetailValue(dataKey, val);
    if (formatted === 'N/A') return null;
    const colorClass = colorFn ? colorFn(val) : '';
    return (
      <div className="flex justify-between">
        <span className="text-gray-500">{label}:</span>
        <span className={`font-medium ${colorClass}`}>{formatted}</span>
      </div>
    );
  };

  // RSI color helper (matches detail view)
  const rsiColor = (v) => {
    const num = typeof v === 'string' ? Number(v) : v;
    if (typeof num !== 'number' || Number.isNaN(num)) return '';
    return num <= 30 ? 'text-red-600' : num >= 70 ? 'text-green-600' : '';
  };

  // Hover help on the symbol name: Sector, Industry, and the business
  // description the nightly pipeline captures from the FMP profile.
  const companyDescription = String(stock.companyDescription ?? stock.company_description ?? '').trim();
  const symbolHelp = [
    stock.sector ? `Sector: ${stock.sector}` : null,
    stock.industry ? `Industry: ${stock.industry}` : null,
    companyDescription || null,
  ].filter(Boolean).join('\n');

  return (
    <div className="ste-card group">
      {/* Card Header with Signal Badge */}
      <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-navy-900 mb-1" data-testid="chart-card-symbol" title={symbolHelp || undefined}>{stock.symbol}</h3>
            <p className="text-xs text-gray-600 truncate">{stock.companyName}</p>
          </div>
          
          {/* Trend Category Badge */}
          <div className="flex flex-col items-end ml-3">
            <div className={`px-3 py-1.5 rounded-lg font-bold text-xs ${trendCategory.color}`}>
              {trendCategory.label}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              SS: {stock.signal_score}/5
            </div>
          </div>
        </div>

        {/* Predicted Direction */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center space-x-2">
            <span className="text-gray-500">Predicted:</span>
            <div className={`flex items-center font-semibold ${
              stock.predicted_direction === '↑' || stock.predicted_direction === 'up' ? 'text-signal-bullish' : 'text-signal-bearish'
            }`}>
              {stock.predicted_direction === '↑' || stock.predicted_direction === 'up' ? '↑' : '↓'}
              <span className="ml-1">{stock.predicted_signal_score}/5</span>
            </div>
          </div>
          <div className="text-gray-400">
            {stock.n_days_since_cross_over}d trend
          </div>
        </div>
      </div>

      {/* Chart Image */}
      <div 
        className="relative aspect-video bg-gray-50 cursor-pointer overflow-hidden group-hover:bg-gray-100 transition-colors"
        onClick={handleChartClick}
        title="Click to view larger chart"
      >
        {imageError || !imageUrl ? (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="mt-2 text-sm">Chart not available</p>
              <p className="text-xs mt-1">Click for details</p>
            </div>
          </div>
        ) : (
          <>
            <img
              src={imageUrl}
              alt={`${stock.symbol} chart`}
              className="w-full h-full object-contain"
              referrerPolicy="no-referrer"
              onError={handleImageError}
              loading="lazy"
            />
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white rounded-lg px-4 py-2 shadow-lg">
                <span className="text-sm font-medium text-gray-700">View Details →</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Card Content */}
      <div className="p-4">
        {/* Always-visible summary strip */}
        <div className="grid grid-cols-3 gap-3 text-sm mb-3">
          <div className="min-w-0">
            <span className="text-gray-500 text-xs">Trend:</span>
            <p className="font-medium text-gray-900">{stock.trend_comment || 'N/A'}</p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500 text-xs">RSI:</span>
            <p className={`font-medium ${rsiColor(stock.rsi) || 'text-gray-900'}`}>
              {stock.rsi != null ? Number(stock.rsi).toFixed(1) : 'N/A'}
            </p>
          </div>
          <div className="min-w-0">
            <span className="text-gray-500 text-xs">% from 6M High:</span>
            <p className="font-medium text-gray-900">
              {formatChartDetailValue('pcnt_upside_in_range_6m', stock.pcnt_upside_in_range_6m)}
            </p>
          </div>
        </div>

        {/* Sector / Industry */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
          <span className="px-2 py-0.5 bg-gray-100 rounded">{stock.sector}</span>
          <span className="px-2 py-0.5 bg-gray-100 rounded">{stock.industry}</span>
        </div>

        {/* Expandable Details — same sections & metrics as ChartDetailView */}
        <div className={`overflow-hidden transition-all duration-300 ${expanded ? 'max-h-[3000px]' : 'max-h-0'}`}>
          <div className="pt-3 border-t border-gray-200 space-y-4 text-xs">

            {/* ── Trend Information ── */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 text-sm">Trend Information</h4>
              <div className="space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-gray-500">Trend State:</span>
                  <span className={`font-medium ${parseInt(stock.trend_state) === 1 ? 'text-green-600' : parseInt(stock.trend_state) === 0 ? 'text-red-600' : ''}`}>
                    {parseInt(stock.trend_state) === 1 ? 'UpTrend' : parseInt(stock.trend_state) === 0 ? 'DownTrend' : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Days Since Crossover:</span>
                  <span className={`font-medium ${Number(stock.n_days_since_cross_over) < 0 ? 'text-red-600' : Number(stock.n_days_since_cross_over) > 0 ? 'text-green-600' : ''}`}>
                    {stock.n_days_since_cross_over}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Trend Comment:</span>
                  <span className="font-medium">{stock.trend_comment || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Signal Score:</span>
                  <span className="font-medium">{stock.signal_score}/5</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Predicted Score:</span>
                  <span className="font-medium">{stock.predicted_signal_score}/5</span>
                </div>
                {/* % Away from 6M High moved up to the always-visible summary strip */}
              </div>
            </div>

            {/* ── Technical Indicators ── */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 text-sm">Technical Indicators</h4>
              <div className="space-y-1.5">
                <CardMetricRow label="RSI" dataKey="rsi" colorFn={rsiColor} />
                {stock.sma200_slope != null && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">SMA200 Slope:</span>
                    <span className={`font-medium ${stock.sma200_slope > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {stock.sma200_slope > 0 ? '↑ Positive' : '↓ Negative'}
                    </span>
                  </div>
                )}
                <CardMetricRow label="% from SMA200" dataKey="pcnt_from_sma200" />
              </div>
            </div>

            {/* ── Financial Metrics ── */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 text-sm">Financial Metrics</h4>
              <div className="space-y-1.5">
                {/* P/S: good ≤2, bad >8 */}
                <CardMetricRow label="P/S Ratio" dataKey="priceSalesRatioTTM"
                  colorFn={(v) => v <= 2 ? 'text-green-600' : v > 8 ? 'text-red-600' : ''} />
                {/* P/E: good 0–15, bad <0 or >40 */}
                <CardMetricRow label="P/E Ratio" dataKey="priceEarningsRatioTTM"
                  colorFn={(v) => (v > 0 && v <= 15) ? 'text-green-600' : (v < 0 || v > 40) ? 'text-red-600' : ''} />
                {/* PEG: good 0–1, bad <0 or >2 */}
                <CardMetricRow label="PEG Ratio" dataKey="priceEarningsToGrowthRatioTTM"
                  colorFn={pegRatioColorClass} />
              </div>
            </div>

            {/* ── Growth & Valuation ── */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 text-sm">Growth & Valuation</h4>
              <div className="space-y-1.5">
                {/* Forecast growth: good ≥10%, bad <0% */}
                <CardMetricRow label="Forecast Revenue Growth" dataKey="forecast_next_year_revenue_growth_rate"
                  colorFn={(v) => v >= 0.10 ? 'text-green-600' : v < 0 ? 'text-red-600' : ''} />
                <CardMetricRow label="Forecast Profit Growth" dataKey="forecast_next_year_profit_growth_rate"
                  colorFn={(v) => v >= 0.10 ? 'text-green-600' : v < 0 ? 'text-red-600' : ''} />
                {/* Rule of 40: good ≥40, bad <20 */}
                <CardMetricRow label="Rule of 40" dataKey="rule_of_40"
                  colorFn={(v) => Math.round(v * 100) >= 40 ? 'text-green-600' : ''} />
                {/* Top Holdings */}
                {stock.holdings != null && stock.holdings !== '' && (
                  <div className="pt-1">
                    <span className="text-gray-500">Holdings:</span>
                    <p className="font-medium mt-1 break-words">{stock.holdings}</p>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Expand/Collapse Button */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full mt-3 text-sm text-brand-500 hover:text-brand-600 focus:outline-none font-medium"
        >
          {expanded ? '↑ Show Less' : '↓ Show More'}
        </button>
      </div>
    </div>
  );
};

export default ChartCard;
